import React, { useEffect, useRef, useState, type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Button, FormControl, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Download from '@mui/icons-material/Download';

import Collapse from '@mui/material/Collapse';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useLocation, useParams } from 'react-router-dom';
import { useIsMount } from '@fiji/common/src/hooks';
import { getTimeInFromTo, getUniqueDeviceTypeIds } from '@fiji/common/src/utils/helpers';
import { useDispatch } from 'react-redux';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';

import { CustomTransComponent, Loader } from 'components';
import { ExportModal } from 'pages/common/ExportModal';

import moment from 'moment';
import { CHART_TYPE } from 'pages/WidgetManagement/common';
import { ArrowBackIos, ArrowDropDown, ArrowForwardIos } from '@mui/icons-material';
import { seriesColors, TIME_BTN } from '@fiji/common';
import { useTransformData } from 'hooks/useTransformData';
import { MultiColumnList } from 'components/MultiColumnList';
import { useGetWidgetByIdQuery } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { setAppHeaderSubTitle, setAppHeaderTitle } from '@fiji/common/src/features/common/commonSlice';
import {
    useGetDeviceByIdQuery,
    useGetDeviceTrendsMutation,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useTranslation } from 'react-i18next';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { useGetLoadGroupsListAllQuery } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import {
    useLazyGetLoadTrendConfigurationQuery,
    useGetLoadTrendUnitsQuery,
} from '@fiji/common/src/features/deviceManagement/ontologyApi';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export const loadColors: string[] = [
    '#F0CB2F',
    '#F47721',
    '#39B620',
    '#983FEF',
    '#004B9E',
    '#87C6D4',
    '#9C0E33',
    '#008A7E',
    '#774E08',
    '#E91E63',
    '#3F51B5',
    '#009688',
    '#FF5722',
    '#607D8B',
    '#795548',
    '#CDDC39',
];

/**
 * FAKE LOAD GROUP DATA
 */

export const MultiLoadTrends = (): JSX.Element => {
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const { state = {} } = useLocation();
    const handleLoadGroupsPayload = (config: any): any[] => {
        const payload: any = {
            page: 0,
            size: 25,
        };

        const safeConfig = config || {};
        const selectedNodes = Array.isArray(safeConfig.selectedNodes) ? safeConfig.selectedNodes : [];
        if (selectedNodes.length) {
            const selectedIds = selectedNodes.filter((item: any) => item && item.id).map((item: any) => item.id);
            payload['ids'] = selectedIds;
            return payload;
        }

        if (state['deviceId']) payload['deviceId'] = [state['deviceId']];
        if (state['groupId']) payload['groupId'] = [state['groupId']];

        if (!safeConfig.isAutomated) {
            const secondary = Array.isArray(safeConfig.secondary) ? safeConfig.secondary : [];
            payload['ids'] = secondary.filter((item: any) => item && item.id).map((item: any) => item.id);
        }

        return payload;
    };

    /**
     * Legend data formatted for MultiColumnList
     */
    const [legendData, setLegendData] = useState<any[]>([]);
    const [selectedLoads, setSelectedLoads] = useState<any>([]);

    // new start
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canDownloadLoadsGroupsTrends = hasPermission('load-telemetry_create-devices');
    const canDownloadLoadsGroupsTrendsExpired = hasPermission('load-telemetry_create-devices_hasExpired');

    const theme: any = useTheme();
    const { t } = useTranslation();
    const [isCalender, setIsCalender] = React.useState(false);
    const isMount = useIsMount();
    const dispatch = useDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const [openSideBar, setOpenSideBar] = React.useState(true);
    const [period, setPeriod] = React.useState<any>();
    const [date, setDate]: any = useState();
    const [timeRange, setTimeRange] = React.useState<{ from: any; to: any }>({
        ...getTimeInFromTo(24, 'h'),
    });
    const [activeTime, setActiveTime] = React.useState<{ from: any; to: any }>({
        ...getTimeInFromTo(24, 'h'),
    });
    const [unit, setUnit] = React.useState<any>('');
    const modalRef: any = React.useRef(null);

    const { widgetId }: any = useParams();
    const { data: widgetDetails }: any = useGetWidgetByIdQuery(widgetId, {
        skip: !widgetId || !currentOrg?.id,
    });
    const { data: sourceDeviceData }: any = useGetDeviceByIdQuery(state['deviceId'], {
        skip: !state['deviceId'] || !currentOrg?.id,
    });
    const { data: loadGroupLists }: any = useGetLoadGroupsListAllQuery(
        handleLoadGroupsPayload(widgetDetails?.data?.config),
        {
            skip: !widgetDetails,
        }
    );
    const { pointConversion, timezoneOffset, convertDateTime } = useTransformData();

    const [getDeviceTrends, { isLoading, data: trendsData }] = useGetDeviceTrendsMutation();

    const [channelIds, setChannelIds] = useState<any>([]);
    const setChannelIdsIfChanged = React.useCallback((nextChannelIds: any): void => {
        setChannelIds((prevChannelIds: any) => {
            const prevPayload = JSON.stringify(prevChannelIds ?? []);
            const nextPayload = JSON.stringify(nextChannelIds ?? []);
            return prevPayload === nextPayload ? prevChannelIds : nextChannelIds;
        });
    }, []);
    const [activeDuration, setActiveDuration] = useState<number>(24);
    const [isTimeRangeActive, setIsTimeRangeActive] = useState(false);
    const [hasNoTrendsData, setHasNoTrendsData] = useState(false);
    const chartRef: any = useRef(null);
    useNewCustomTranslations('DASHBOARD_MANAGEMENT');
    const [options, setOptions] = useState<any>({});

    const handleSideBar = (): void => {
        setOpenSideBar((prev) => !prev);
    };

    const [getLoadTrendConfiguration, { data: configResponse }] = useLazyGetLoadTrendConfigurationQuery();
    const getDeviceType = (): any[] => {
        if (selectedGroupId) {
            const selectedLoadGroups = legendData?.find((item: any) => item?.id === selectedGroupId);
            return getUniqueDeviceTypeIds(selectedLoadGroups?.loadConfig);
        }
        return getUniqueDeviceTypeIds(selectedLoads);
    };
    const selectedGroup = legendData?.find((item: any) => item?.id === selectedGroupId) ?? legendData?.[0] ?? null;
    const selectedModelCategory = selectedGroup?.modelCategory;
    const { data: unitsResponse, isFetching: isFetchingUnits } = useGetLoadTrendUnitsQuery(getDeviceType(), {
        skip: !getDeviceType()?.length,
    });

    useEffect(() => {
        if (selectedModelCategory) {
            void getLoadTrendConfiguration({ modelCategory: selectedModelCategory });
        }
    }, [getLoadTrendConfiguration, selectedModelCategory]);

    const getTrendsPayload = (loadConfig: any): any => {
        if (!configResponse?.data) return [];
        const payload: any = [];
        const units = Object.keys(unitsResponse?.data ?? {});
        const selectedUnit = unit || units?.[0];
        if (!selectedUnit) return payload;
        loadConfig?.forEach((item: any) => {
            const index = payload?.findIndex((subItem: any) => subItem?.deviceId === item?.deviceId);
            const channelId = configResponse?.data?.[item?.deviceTypeId]?.[`${item?.loadId}_${selectedUnit}`];
            if (channelId) {
                if (index === -1) {
                    payload.push({
                        deviceId: item?.deviceId,
                        channelId: [channelId],
                    });
                } else {
                    payload[index].channelId?.push(channelId);
                }
            }
        });
        return payload;
    };

    React.useEffect(() => {
        if (!isMount && channelIds) {
            getDeviceTrends({
                loadTrend: true,
                payload: channelIds,
                ...(date?.[0]
                    ? {
                          from: new Date(date[0])?.getTime(),
                          to: new Date(date[1])?.getTime(),
                      }
                    : timeRange),
            }) as any;
        }
    }, [channelIds]);

    React.useEffect(() => {
        if (widgetDetails) {
            dispatch(setAppHeaderTitle(widgetDetails?.data?.name));
            dispatch(
                setAppHeaderSubTitle(state['deviceName'] ?? sourceDeviceData?.data?.name ?? widgetDetails?.data?.name)
            );
            if (widgetDetails?.data?.config?.duration) {
                activeDurationHandler(widgetDetails?.data?.config?.duration);
            }
        }
        return (): any => {
            dispatch(setAppHeaderTitle(''));
            dispatch(setAppHeaderSubTitle(''));
        };
    }, [widgetDetails, sourceDeviceData?.data?.name, state['deviceName']]);

    useEffect(() => {
        if (loadGroupLists?.data?.records?.length) {
            const defaultGroupId = loadGroupLists?.data?.records?.[0]?.id;
            setSelectedGroupId(defaultGroupId);
            const newLegendData = loadGroupLists?.data?.records?.map((group: any) => ({
                ...group,
                loadConfig: group.loadConfig.map((ch: any, index: any) => ({
                    ...ch,
                    color: loadColors[index],
                    visible: group?.id === defaultGroupId,
                })),
            }));
            setLegendData(newLegendData);
            const selectedLegend = newLegendData?.find((group: any) => group?.id === defaultGroupId);
            setSelectedLoads(selectedLegend?.loadConfig ?? []);
        }
    }, [loadGroupLists]);

    useEffect(() => {
        if (!isFetchingUnits && unitsResponse && configResponse?.data) {
            const selectedLegend = legendData?.find((item: any) => item?.id === selectedGroupId);
            const units = Object.keys(unitsResponse?.data ?? {});
            if (units?.length) {
                setUnit((prevUnit: string) => (prevUnit && units.includes(prevUnit) ? prevUnit : units[0]));
            }
            if (selectedLegend) {
                setChannelIdsIfChanged(getTrendsPayload(selectedLegend?.loadConfig));
            }
        }
    }, [unitsResponse, configResponse?.data, selectedGroupId, setChannelIdsIfChanged]);

    const activeDurationHandler = (hour: number): void => {
        const time = getTimeInFromTo(hour, 'h');
        if (hour > 24) {
            setIsTimeRangeActive(true);
            setPeriod([moment(time?.from) ?? null, moment(time?.to) ?? null]);
            setDate([moment(time?.from) ?? null, moment(time?.to) ?? null]);
        } else {
            setIsTimeRangeActive(false);
            setDate([null, null]);
            setPeriod([null, null]);
        }
        setActiveDuration(hour);
        setTimeRange(time);
    };

    const dateChangeHandler = (selectedPeriod: any = period): void => {
        if (!isMount && channelIds?.length && selectedPeriod?.[0] && selectedPeriod?.[1]) {
            setActiveTime({
                from: new Date(selectedPeriod?.[0])?.getTime() === 0 ? null : new Date(selectedPeriod?.[0])?.getTime(),
                to: new Date(selectedPeriod?.[1])?.getTime() === 0 ? null : new Date(selectedPeriod?.[1])?.getTime(),
            });
            getDeviceTrends({
                loadTrend: true,
                payload: channelIds,
                from: new Date(selectedPeriod?.[0])?.getTime() === 0 ? null : new Date(selectedPeriod?.[0])?.getTime(),
                to: new Date(selectedPeriod?.[1])?.getTime() === 0 ? null : new Date(selectedPeriod?.[1])?.getTime(),
            }) as any;
        }
    };

    const getDisplayName = (item: any): any =>
        selectedLoads?.find(
            (subItem: any) =>
                subItem?.deviceId === item?.deviceId &&
                configResponse?.data?.[subItem?.deviceTypeId]?.[`${subItem?.loadId}_${unit}`] === item?.channelId
        );

    // Finds the typical reporting cadence for this series so an abnormally large gap
    // (a period where no data was reported at all) can be told apart from normal
    // sampling jitter. The median is too easily skewed by dense clusters of points,
    // so we use a high percentile (95th) of the observed gaps as the "typical max"
    // interval, then apply a generous multiplier - real outages are orders of
    // magnitude larger than jitter, so this avoids flagging jitter as a gap.
    const getGapBreakThreshold = (points: Array<[number, number]>): number => {
        const diffs: number[] = [];
        for (let i = 1; i < points.length; i++) {
            const diff = points[i][0] - points[i - 1][0];
            if (diff > 0) diffs.push(diff);
        }
        if (!diffs.length) return Infinity;
        diffs.sort((a, b) => a - b);
        const typicalMaxInterval = diffs[Math.min(Math.floor(diffs.length * 0.95), diffs.length - 1)];
        return typicalMaxInterval > 0 ? typicalMaxInterval * 10 : Infinity;
    };

    // ECharts only breaks a line/area at an explicit `null` value (see `connectNulls`).
    // Since a missing time period simply has no records at all (not a null-valued one),
    // we insert a synthetic null point right after the last known point of each gap so
    // ECharts stops connecting/filling across periods where no data was reported.
    const insertGapBreaks = (rawPoints: any[]): any[] => {
        if (!rawPoints?.length) return rawPoints;
        const points = rawPoints
            .map((point: any): [number, any] => [Number(point?.[0]), point?.[1]])
            .filter((point) => !Number.isNaN(point[0]))
            .sort((a, b) => a[0] - b[0]);
        const threshold = getGapBreakThreshold(points);
        const result: any[] = [];
        points.forEach((point, index) => {
            result.push(point);
            const next = points[index + 1];
            if (next && next[0] - point[0] > threshold) {
                result.push([point[0] + 1, null]);
            }
        });
        return result;
    };

    const getSeriesData = (data: any): any[] =>
        data?.map((trend: any, index: number) => {
            const seletedData = getDisplayName(trend);
            const chartType = widgetDetails?.data?.config?.type;
            return {
                id: `${trend?.deviceId}-${trend?.channelId}`,
                name: seletedData?.name ?? '',
                data: insertGapBreaks(trend?.records?.map((point: any): any => [point?.x, point?.y]) ?? []),
                type: chartType === 'area' ? 'line' : chartType ?? 'line',
                connectNulls: false,
                ...(chartType === 'area' && { areaStyle: { opacity: 0.8 } }),
                lineStyle: { color: seriesColors[index] },
                itemStyle: { color: seriesColors[index] },
                showSymbol: false,
            };
        });

    const flattenChannels = (data: any): any =>
        data?.flatMap((item: any) =>
            item?.channels?.map((channel: any) => ({
                ...channel,
                deviceId: item?.deviceId,
            }))
        );

    // Rounds a rough step (max / targetSplits) up to a "nice" number (1, 2, 5, or 10 x 10^n)
    // so axis ticks land on readable values instead of odd fractions.
    const getNiceStep = (roughStep: number): number => {
        if (!roughStep || roughStep <= 0) return 1;
        const exponent = Math.floor(Math.log10(roughStep));
        const magnitude = 10 ** exponent;
        const fraction = roughStep / magnitude;
        let niceFraction = 10;
        if (fraction <= 1) niceFraction = 1;
        else if (fraction <= 2) niceFraction = 2;
        else if (fraction <= 5) niceFraction = 5;
        return niceFraction * magnitude;
    };

    // Instead of stopping the axis flush at the tightly-fit "nice" max (e.g. 100 when the
    // highest value is 90), extend it by one more step so the topmost data point isn't
    // pinned to the very top of the chart (e.g. 90 -> 120 with a step of 20).
    const getYAxisMax = (maxValue: number, targetSplits = 5): number | undefined => {
        if (!maxValue || maxValue <= 0) return undefined;
        const step = getNiceStep(maxValue / targetSplits);
        const niceMax = Math.ceil(maxValue / step) * step;
        return niceMax + step;
    };

    useEffect(() => {
        const data = flattenChannels(trendsData?.data);
        const instance = chartRef?.current?.getEchartsInstance?.();
        if (data?.length && !isLoading) {
            setHasNoTrendsData(false);
            instance?.hideLoading();
            const seriesData = getSeriesData(data);
            const maxValue = data.reduce((max: number, trend: any) => {
                const trendMax =
                    trend?.records?.reduce(
                        (recordMax: number, point: any) => Math.max(recordMax, Number(point?.y) || 0),
                        0
                    ) ?? 0;
                return Math.max(max, trendMax);
            }, 0);
            setOptions({
                xAxis: {
                    type: 'time',
                    axisLabel: {
                        formatter: (value: number) => {
                            const timeLabel = convertDateTime({ timestamp: value, customFormat: 'HH:mm' });
                            return timeLabel === '00:00'
                                ? convertDateTime({ timestamp: value, customFormat: 'DD MMM' })
                                : timeLabel;
                        },
                    },
                },
                yAxis: {
                    type: 'value',
                    max: getYAxisMax(maxValue),
                    axisLabel: {
                        formatter: (value: number) => {
                            const unitStr = data[0]?.unit ? ` ${data[0].unit}` : '';
                            return `${pointConversion(value)}${unitStr}`;
                        },
                    },
                },
                series: seriesData,
                tooltip: {
                    trigger: 'axis',
                    formatter: (params: any) => {
                        if (!Array.isArray(params) || !params.length) return '';
                        const header = convertDateTime({
                            timestamp: params[0].value[0],
                            customFormat: 'dddd, D MMM, HH:mm:ss',
                        });
                        const lines = params.map((p: any) => {
                            const trend = data[p.seriesIndex];
                            const unitSuffix = trend?.unit ? ` ${trend.unit}` : '';
                            return `${p.marker} ${p.seriesName}: <b>${pointConversion(p.value[1])}${unitSuffix}</b>`;
                        });
                        return `${header}<br/>${lines.join('<br/>')}`;
                    },
                },
                // legend: { show: true },
                grid: { left: '5%', right: '5%', bottom: '5%' },
                dataZoom: [
                    {
                        type: 'inside',
                        xAxisIndex: 0,
                        zoomOnMouseWheel: true,
                        moveOnMouseWheel: false,
                        moveOnMouseMove: false,
                    },
                    // { type: 'slider', xAxisIndex: 0 },
                ],
            });
        } else if (instance) {
            setHasNoTrendsData(true);
            instance.setOption({ series: [] }, true);
            instance.showLoading('default', { text: '' });
        }
        return () => {
            const inst = chartRef?.current?.getEchartsInstance?.();
            inst?.setOption?.({ series: [] }, true);
        };
    }, [trendsData, isLoading]);

    React.useEffect(() => {
        if (!isMount && channelIds?.length) {
            setActiveTime(timeRange);
            getDeviceTrends({
                loadTrend: true,
                payload: channelIds,
                ...timeRange,
            }) as any;
        }
    }, [timeRange]);

    const isTimeRangeDisabled = (): boolean => {
        if (isTimeRangeActive) {
            return moment(moment().format('MM/DD/YY')).isSameOrBefore(
                moment(new Date(date[1] ?? 0)?.getTime() ?? moment()).format('MM/DD/YY')
            );
        }
        return moment(moment().format('MM/DD/YY')).isSameOrBefore(moment(timeRange?.to ?? moment()).format('MM/DD/YY'));
    };

    const getUTCTime = (): any => ({
        from:
            moment
                .utc(moment(activeTime?.from ?? null).format('"MM/DD/YYYY"'), 'MM/DD/YYYY')
                .startOf('day')
                .valueOf() +
            timezoneOffset * 60 * 1000,
        to:
            moment
                .utc(moment(activeTime?.to ?? null).format('"MM/DD/YYYY"'), 'MM/DD/YYYY')
                .endOf('day')
                .valueOf() +
            timezoneOffset * 60 * 1000,
    });

    // new end

    /**
     * Handles both:
     * - Parent radio click
     * - Child checkbox click
     */
    const changeHandler = (e: any, id: string, parentId?: string): void => {
        // RADIO (parent group change)
        if (!parentId) {
            setSelectedGroupId(id);

            setLegendData((prev) => {
                const updated = prev.map((group) => ({
                    ...group,
                    loadConfig: group.loadConfig.map((child: any) => ({
                        ...child,
                        visible: group.id === id, // only selected group visible
                    })),
                }));

                const selectedGroups = updated.find((g) => g.id === id);
                const loads = selectedGroups?.loadConfig ?? [];

                setSelectedLoads(loads);

                return updated;
            });

            return;
        }

        // CHECKBOX (child toggle)
        setLegendData((prev) => {
            const updated = prev.map((group) =>
                group.id === parentId
                    ? {
                          ...group,
                          loadConfig: group.loadConfig.map((child: any) =>
                              child.id === id ? { ...child, visible: e.target.checked } : child
                          ),
                      }
                    : group
            );

            const selectedGroups = updated.find((g) => g.id === parentId);
            const visibleLoads = selectedGroups?.loadConfig?.filter((c: any) => c.visible) ?? [];

            setSelectedLoads(visibleLoads);
            setChannelIdsIfChanged(getTrendsPayload(visibleLoads));

            return updated;
        });
    };

    const unitChangeHandler = (e: any): void => {
        setUnit(e.target.value);

        const payload: any = [];
        selectedLoads.forEach((item: any) => {
            const channelId = configResponse?.data?.[item?.deviceTypeId]?.[`${item?.loadId}_${e.target.value}`];
            if (!channelId) return;
            const index = payload.findIndex((p: any) => p.deviceId === item.deviceId);

            if (index === -1) {
                payload.push({ deviceId: item.deviceId, channelId: [channelId] });
            } else {
                payload[index].channelId.push(channelId);
            }
        });

        setChannelIdsIfChanged(payload);
    };

    return (
        <>
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                className="w-100 bg-white trends-wrapper"
                spacing={2}
            >
                {/* MAIN CONTENT (chart etc.) */}
                <Stack className={`${openSideBar ? 'w-75' : 'w-100'}`}>
                    {/* Header */}
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} m={3}>
                        <Stack direction={'row'} alignItems={'center'} spacing={8}>
                            <Stack direction={'row'} alignItems={'center'} spacing={1} px={1}>
                                <IconButton
                                    disabled={!channelIds?.length}
                                    onClick={(): void => {
                                        if (isTimeRangeActive) {
                                            setActiveTime({
                                                from: date?.[0] ? new Date(date[0])?.getTime() - 86400000 : null,
                                                to: date?.[1] ? new Date(date[1])?.getTime() - 86400000 : null,
                                            });
                                            getDeviceTrends({
                                                loadTrend: true,
                                                payload: channelIds,
                                                from: date?.[0] ? new Date(date[0])?.getTime() - 86400000 : null,
                                                to: date?.[1] ? new Date(date[1])?.getTime() - 86400000 : null,
                                            }) as any;

                                            setDate([
                                                date?.[0] ? moment(new Date(date[0])?.getTime() - 86400000) : null,
                                                date?.[1] ? moment(new Date(date[1])?.getTime() - 86400000) : null,
                                            ]);
                                        } else
                                            setTimeRange((prop: { from: number; to: number }) => ({
                                                from: (prop?.from ?? 0) - 86400000,
                                                to: (prop?.to ?? 0) - 86400000,
                                            }));
                                    }}
                                >
                                    <ArrowBackIos className="height-16 width-16" color="primary" />
                                </IconButton>

                                <FormControl fullWidth sx={{ minWidth: 160 }}>
                                    <Select
                                        value={
                                            activeDuration
                                                ? convertDateTime({
                                                      timestamp: isTimeRangeActive
                                                          ? date?.[0]
                                                          : timeRange?.to ?? Date?.now(),
                                                      customFormat: 'MMM D, YYYY',
                                                  })
                                                : moment(
                                                      isTimeRangeActive ? date?.[0] : timeRange?.to ?? Date?.now()
                                                  ).format('MMM D, YYYY')
                                        }
                                        renderValue={() =>
                                            activeDuration
                                                ? convertDateTime({
                                                      timestamp: isTimeRangeActive
                                                          ? date?.[0]
                                                          : timeRange?.to ?? Date?.now(),
                                                      customFormat: 'MMM D, YYYY',
                                                  })
                                                : moment(
                                                      isTimeRangeActive ? date?.[0] : timeRange?.to ?? Date?.now()
                                                  ).format('MMM D, YYYY')
                                        }
                                        variant="outlined"
                                        onOpen={(): void => setIsCalender(true)}
                                        open={isCalender}
                                        inputProps={{ IconComponent: () => null }}
                                        disabled={!channelIds?.length}
                                        // onClose={(): void => setIsCalender(false)}
                                    >
                                        <Stack justifyContent={'center'} alignSelf={'center'}>
                                            <Stack
                                                justifyContent={'center'}
                                                alignContent={'center'}
                                                display={'flex'}
                                                flexDirection={'row'}
                                                m={1}
                                            >
                                                <TextField
                                                    id="outlined-basic"
                                                    value={
                                                        activeDuration
                                                            ? convertDateTime({
                                                                  timestamp: period?.[0] ?? activeTime?.from,
                                                                  customFormat: 'MMM D, YYYY',
                                                              })
                                                            : moment(
                                                                  period?.[0] ?? new Date().setHours(0, 0, 0, 0)
                                                              ).format('MMM D, YYYY')
                                                    }
                                                    label=""
                                                    variant="outlined"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{ padding: '12px 2px' }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    sx={{ alignSelf: 'center', fontWeight: 400, margin: '8px' }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:TO'} />
                                                </Typography>
                                                <TextField
                                                    id="outlined-basic"
                                                    value={
                                                        activeDuration
                                                            ? convertDateTime({
                                                                  timestamp: period?.[1] ?? activeTime?.to,
                                                                  customFormat: 'MMM D, YYYY',
                                                              })
                                                            : moment(
                                                                  period?.[1] ?? new Date().setHours(23, 59, 59, 0)
                                                              ).format('MMM D, YYYY')
                                                    }
                                                    label=""
                                                    variant="outlined"
                                                    inputProps={{ readOnly: true }}
                                                    sx={{ padding: '12px 2px' }}
                                                />
                                            </Stack>
                                            <Stack
                                                sx={{
                                                    '& .MuiDateRangeCalendar-root': { alignSelf: 'center' },
                                                    alignSelf: 'center',
                                                }}
                                            >
                                                <StaticDateRangePicker
                                                    disableFuture={true}
                                                    value={period ?? [null, null]}
                                                    onChange={(dateData: any): void => {
                                                        setPeriod([dateData[0] ?? null, dateData[1] ?? null]);
                                                    }}
                                                    // calendars={1}
                                                    slotProps={{
                                                        actionBar: { actions: [] },
                                                        toolbar: { hidden: true },
                                                    }}
                                                />
                                            </Stack>
                                            <Stack
                                                justifyContent={'flex-end'}
                                                alignContent={'center'}
                                                display={'flex'}
                                                flexDirection={'row'}
                                                m={2}
                                            >
                                                <Button
                                                    sx={{
                                                        marginRight: '8px',
                                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                                        '&:hover': {
                                                            backgroundColor: theme?.palette?.primary?.[50],
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: theme?.palette?.primary?.[50],
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={(): void => {
                                                        setPeriod(date);
                                                        setIsCalender(false);
                                                    }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:CANCEL'} />
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    disabled={!period?.[0] || !period?.[1]}
                                                    onClick={(): void => {
                                                        setDate(period);
                                                        dateChangeHandler(period);
                                                        setIsCalender(false);
                                                        setIsTimeRangeActive(true);
                                                        setActiveDuration(0);
                                                    }}
                                                    sx={{
                                                        backgroundColor: theme?.palette?.primary?.main,
                                                        '&:hover': {
                                                            backgroundColor: theme?.palette?.primary?.main,
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: theme?.palette?.primary?.[200],
                                                            backgroundColor: theme?.palette?.primary?.[50],
                                                        },
                                                    }}
                                                >
                                                    <CustomTransComponent translationKey={'COMMON:APPLY'} />
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Select>
                                </FormControl>
                                {isTimeRangeActive && (
                                    <>
                                        {' '}
                                        <Typography>To</Typography>
                                        <FormControl fullWidth sx={{ minWidth: 160 }}>
                                            <Select
                                                value={
                                                    activeDuration
                                                        ? convertDateTime({
                                                              timestamp: timeRange?.from ?? null,
                                                              customFormat: 'MMM D, YYYY',
                                                          })
                                                        : moment(timeRange?.from ?? null).format('MMM D, YYYY')
                                                }
                                                renderValue={() =>
                                                    activeDuration
                                                        ? convertDateTime({
                                                              timestamp: date?.[1] ?? null,
                                                              customFormat: 'MMM D, YYYY',
                                                          })
                                                        : moment(date?.[1] ?? null).format('MMM D, YYYY')
                                                }
                                                variant="outlined"
                                                onOpen={(): void => setIsCalender(true)}
                                                inputProps={{ IconComponent: () => null }}
                                                disabled={!channelIds?.length}
                                                // onClose={(): void => setIsCalender(false)}
                                            ></Select>
                                        </FormControl>
                                    </>
                                )}
                                <IconButton
                                    disabled={isTimeRangeDisabled() || !channelIds?.length}
                                    onClick={(): void => {
                                        if (isTimeRangeActive) {
                                            setActiveTime({
                                                from: date?.[0] ? new Date(date[0])?.getTime() + 86400000 : null,
                                                to: date?.[1] ? new Date(date[1])?.getTime() + 86400000 : null,
                                            });
                                            getDeviceTrends({
                                                loadTrend: true,
                                                payload: channelIds,
                                                from: date?.[0] ? new Date(date[0])?.getTime() + 86400000 : null,
                                                to: date?.[1] ? new Date(date[1])?.getTime() + 86400000 : null,
                                            }) as any;
                                            setDate([
                                                date?.[0] ? moment(new Date(date[0])?.getTime() + 86400000) : null,
                                                date?.[1] ? moment(new Date(date[1])?.getTime() + 86400000) : null,
                                            ]);
                                        } else
                                            setTimeRange((prop: { from: number; to: number }) => ({
                                                from: (prop?.from ?? 0) + 86400000,
                                                to: (prop?.to ?? 0) + 86400000,
                                            }));
                                    }}
                                >
                                    <ArrowForwardIos
                                        className="height-16 width-16"
                                        {...(!isTimeRangeDisabled() && {
                                            color: 'primary',
                                        })}
                                    />
                                </IconButton>
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'}>
                                {TIME_BTN?.map(
                                    (btn: { btnName: string; btnValue: number }): JSX.Element => (
                                        <Button
                                            key={btn?.btnValue}
                                            disabled={!channelIds?.length}
                                            id={`${btn?.btnValue}`}
                                            variant={activeDuration === btn?.btnValue ? 'contained' : 'outlined'}
                                            sx={{
                                                borderRadius: '0',
                                                backgroundColor:
                                                    activeDuration === btn?.btnValue
                                                        ? theme?.palette?.primary?.main
                                                        : '',
                                                border: `${
                                                    activeDuration === btn?.btnValue
                                                        ? 0
                                                        : `1px solid ${theme?.palette?.primary?.main}`
                                                }`,
                                                '&:hover': {
                                                    backgroundColor:
                                                        activeDuration === btn?.btnValue
                                                            ? theme?.palette?.primary?.main
                                                            : theme?.palette?.primary?.[50],
                                                },
                                            }}
                                            onClick={(): void => activeDurationHandler(btn?.btnValue)}
                                        >
                                            {t(btn?.btnName)}
                                        </Button>
                                    )
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} spacing={4}>
                            <Stack pb={2}>
                                <Box mr={2} sx={{ display: 'flex', justifyContent: 'end' }}>
                                    <FormControl variant="standard">
                                        <Select
                                            value={unit}
                                            onChange={unitChangeHandler}
                                            disableUnderline
                                            IconComponent={ArrowDropDown}
                                            sx={{
                                                minWidth: 100,
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        mt: 1,
                                                        borderRadius: 1,
                                                        boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                                                    },
                                                },
                                            }}
                                            renderValue={() => (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Typography fontWeight={500}>Units</Typography>
                                                </Box>
                                            )}
                                        >
                                            {Object.keys(unitsResponse?.data ?? {}).map((item) => (
                                                <MenuItem
                                                    key={item}
                                                    value={item}
                                                    sx={{
                                                        fontSize: 14,
                                                        '&.Mui-selected': {
                                                            backgroundColor: '#424E540D',
                                                        },
                                                    }}
                                                >
                                                    {unitsResponse?.data[item]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Stack>
                            {(canDownloadLoadsGroupsTrends || canDownloadLoadsGroupsTrendsExpired) && (
                                <Button
                                    id="export"
                                    sx={{
                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                        '&:hover': {
                                            backgroundColor: theme?.palette?.primary?.[50],
                                        },
                                    }}
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<Download />}
                                    onClick={() =>
                                        modalRef?.current?.onClick({
                                            ...activeTime,
                                            payload: channelIds,
                                            ...(!activeDuration && getUTCTime()),
                                            loadTrend: true,
                                            chartType: CHART_TYPE[widgetDetails?.data?.config?.type ?? 'line'],
                                        })
                                    }
                                >
                                    <CustomTransComponent translationKey={'COMMON:EXPORT'} />
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {/* Chart */}
                    <Stack pt={2} sx={{ height: 'calc(100vh - 430px)' }} className="trends-chart-wrapper">
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                                <ReactECharts
                                    ref={chartRef}
                                    option={options}
                                    style={{ height: 'calc(100vh - 430px)' }}
                                    notMerge={true}
                                />
                                {hasNoTrendsData && (
                                    <Typography
                                        data-testid="no-trends-data-available"
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{
                                            position: 'absolute',
                                            top: '45%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            color: '#a5adb1',
                                        }}
                                    >
                                        {t('DASHBOARD_MANAGEMENT:NO_TRENDS_AVAILABLE')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Stack>
                {/* SIDEBAR */}
                {/* Sidebar */}
                <Stack direction={'row'} className={`${openSideBar ? 'w-25' : ''}`} justifyContent={'flex-end'}>
                    <Box className="bg-white border-left-1" sx={{ height: 'calc(100vh - 324px)', width: '100%' }}>
                        <Collapse
                            orientation="horizontal"
                            sx={{
                                width: '100%',
                                '.MuiCollapse-wrapperInner': {
                                    width: '100%',
                                },
                            }}
                            in={openSideBar}
                            collapsedSize={70}
                        >
                            <Stack p={2} className="border-bottom-1">
                                {openSideBar ? (
                                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                        <Stack direction={'row'} alignItems={'center'} spacing={4}>
                                            <ChevronRight
                                                id="right"
                                                onClick={handleSideBar}
                                                className="cursor-pointer"
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                fontSize={'14px'}
                                                fontWeight={'600'}
                                                color="primary"
                                            >
                                                <CustomTransComponent translationKey={'Load Groups'} />
                                            </Typography>
                                        </Stack>
                                        {/* <Edit className="color-content" /> */}
                                    </Stack>
                                ) : (
                                    <ChevronLeft id="left" onClick={handleSideBar} className="cursor-pointer" />
                                )}
                            </Stack>
                            <Stack sx={{ height: 'calc(100vh - 324px)' }} className="overflow-y-scroll">
                                <MultiColumnList
                                    type="radio"
                                    legendData={legendData}
                                    selectedId={selectedGroupId}
                                    changeHandler={changeHandler}
                                    targetKey="id"
                                    isLoading={false}
                                    childKey="loadConfig"
                                    maxItemsPerColumn={100}
                                    multiDevice={true}
                                    isTotalCount={true}
                                />
                            </Stack>
                        </Collapse>
                    </Box>
                </Stack>
            </Stack>
            <ExportModal
                activeDuration={activeDuration}
                loadTrend={true}
                splitFooterActions={true}
                ref={modalRef}
                header={'Export Load Group Trends'}
            />
        </>
    );
};
